import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Search, X, Building2, Star } from 'lucide-react';
import apiClient from '../services/api';
import '../styles/MasterForms.css';

const UserCompanyAccess = () => {
  const [accessList, setAccessList] = useState([]);
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [filteredAccessList, setFilteredAccessList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterUserId, setFilterUserId] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    accessid: null,
    userid: '',
    companyid: '',
    isdefault: false
  });
  const [errors, setErrors] = useState({});
  const [submitLoading, setSubmitLoading] = useState(false);

  useEffect(() => {
    fetchAccessList();
    fetchUsers();
    fetchCompanies();
  }, []);

  useEffect(() => {
    filterAccessList();
  }, [searchTerm, filterUserId, accessList]);

  const fetchAccessList = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/UserCompanyAccess.php');
      
      if (response.data.success) {
        setAccessList(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching company access:', error);
      if (error.response?.status === 401) {
        alert('Session expired. Please login again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await apiClient.get('/Users.php', {
        params: { active: true }
      });
      
      if (response.data.success) {
        setUsers(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchCompanies = async () => {
    try {
      const response = await apiClient.get('/Companies.php', {
        params: { active: true }
      });
      
      if (response.data.success) {
        setCompanies(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching companies:', error);
    }
  };

  const filterAccessList = () => {
    let filtered = accessList;

    // Filter by user
    if (filterUserId) {
      filtered = filtered.filter(access => access.userid === parseInt(filterUserId));
    }

    // Filter by search term
    if (searchTerm.trim()) {
      filtered = filtered.filter(access =>
        access.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
        access.fullname.toLowerCase().includes(searchTerm.toLowerCase()) ||
        access.companyname.toLowerCase().includes(searchTerm.toLowerCase()) ||
        access.companycode.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredAccessList(filtered);
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.userid) {
      newErrors.userid = 'User is required';
    }
    
    if (!formData.companyid) {
      newErrors.companyid = 'Company is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setSubmitLoading(true);
    try {
      const method = editMode ? 'put' : 'post';
      const response = await apiClient[method]('/UserCompanyAccess.php', {
        ...formData,
        userid: parseInt(formData.userid),
        companyid: parseInt(formData.companyid),
        isdefault: formData.isdefault ? 1 : 0
      });
      
      const data = response.data;
      
      if (data.success) {
        alert(editMode ? 'Company access updated successfully!' : 'Company access granted successfully!');
        setShowModal(false);
        resetForm();
        fetchAccessList();
      } else {
        alert('Error: ' + data.message);
      }
    } catch (error) {
      console.error('Error saving company access:', error);
      if (error.response?.status === 401) {
        alert('Session expired. Please login again.');
      } else if (error.response?.status === 409) {
        alert('User already has access to this company.');
      } else {
        alert('Error saving company access. Please try again.');
      }
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleEdit = (access) => {
    setFormData({
      accessid: access.accessid,
      userid: access.userid,
      companyid: access.companyid,
      isdefault: access.isdefault === 1
    });
    setEditMode(true);
    setShowModal(true);
    setErrors({});
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to remove this company access?')) {
      return;
    }
    
    try {
      const response = await apiClient.delete('/UserCompanyAccess.php', {
        data: { accessid: id }
      });
      
      const data = response.data;
      
      if (data.success) {
        alert('Company access removed successfully!');
        fetchAccessList();
      } else {
        alert('Error: ' + data.message);
      }
    } catch (error) {
      console.error('Error deleting company access:', error);
      if (error.response?.status === 401) {
        alert('Session expired. Please login again.');
      } else {
        alert('Error removing company access. Please try again.');
      }
    }
  };

  const handleSetDefault = async (access) => {
    if (access.isdefault === 1) {
      return; // Already default
    }

    if (!window.confirm(`Set ${access.companyname} as default company for ${access.fullname}?`)) {
      return;
    }

    try {
      const response = await apiClient.put('/UserCompanyAccess.php', {
        accessid: access.accessid,
        isdefault: 1
      });
      
      const data = response.data;
      
      if (data.success) {
        alert('Default company updated successfully!');
        fetchAccessList();
      } else {
        alert('Error: ' + data.message);
      }
    } catch (error) {
      console.error('Error updating default company:', error);
      alert('Error updating default company. Please try again.');
    }
  };

  const resetForm = () => {
    setFormData({
      accessid: null,
      userid: '',
      companyid: '',
      isdefault: false
    });
    setEditMode(false);
    setErrors({});
  };

  const openAddModal = () => {
    resetForm();
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    resetForm();
  };

  return (
    <div className="master-container">
      <div className="master-header">
        <div className="header-left">
          <h2>
            <Building2 size={32} style={{ display: 'inline', marginRight: '10px', verticalAlign: 'middle' }} />
            User Company Access
          </h2>
          <p className="subtitle">Manage which companies users can access</p>
        </div>
        <button className="btn-primary" onClick={openAddModal}>
          <Plus size={20} />
          Grant Access
        </button>
      </div>

      <div className="master-toolbar">
        <div className="search-box">
          <Search size={20} />
          <input
            type="text"
            placeholder="Search by user or company name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button className="btn-clear" onClick={() => setSearchTerm('')}>
              <X size={16} />
            </button>
          )}
        </div>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <select
            value={filterUserId}
            onChange={(e) => setFilterUserId(e.target.value)}
            style={{
              padding: '8px 12px',
              border: '2px solid #e5e7eb',
              borderRadius: '8px',
              fontSize: '0.95rem'
            }}
          >
            <option value="">All Users</option>
            {users.map(user => (
              <option key={user.userid} value={user.userid}>
                {user.username} - {user.fullname}
              </option>
            ))}
          </select>
          <div className="toolbar-info">
            Total: {filteredAccessList.length} {filteredAccessList.length === 1 ? 'access' : 'accesses'}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="loading-state">Loading company access...</div>
      ) : filteredAccessList.length === 0 ? (
        <div className="empty-state">
          <p>No company access records found</p>
          {(searchTerm || filterUserId) && (
            <button onClick={() => { setSearchTerm(''); setFilterUserId(''); }}>Clear filters</button>
          )}
        </div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Full Name</th>
                <th>Company</th>
                <th>Default</th>
                <th>Granted At</th>
                <th className="actions-column">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredAccessList.map((access) => (
                <tr key={access.accessid}>
                  <td className="font-semibold">{access.username}</td>
                  <td>{access.fullname}</td>
                  <td>
                    <span className="country-badge">
                      {access.companycode} - {access.companyname}
                    </span>
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    {access.isdefault === 1 ? (
                      <Star size={18} style={{ color: '#f59e0b', fill: '#f59e0b' }} title="Default Company" />
                    ) : (
                      <button
                        onClick={() => handleSetDefault(access)}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: '4px'
                        }}
                        title="Set as default"
                      >
                        <Star size={18} style={{ color: '#9ca3af' }} />
                      </button>
                    )}
                  </td>
                  <td>{new Date(access.createdat).toLocaleDateString()}</td>
                  <td className="actions-column">
                    <button
                      className="btn-icon btn-delete"
                      onClick={() => handleDelete(access.accessid)}
                      title="Remove Access"
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editMode ? 'Edit Company Access' : 'Grant Company Access'}</h3>
              <button className="btn-close" onClick={closeModal}>
                <X size={24} />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="modal-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="userid">
                    User <span className="required">*</span>
                  </label>
                  <select
                    id="userid"
                    value={formData.userid}
                    onChange={(e) => setFormData({ ...formData, userid: e.target.value })}
                    className={errors.userid ? 'error' : ''}
                    disabled={editMode}
                  >
                    <option value="">-- Select User --</option>
                    {users.map(user => (
                      <option key={user.userid} value={user.userid}>
                        {user.username} - {user.fullname}
                      </option>
                    ))}
                  </select>
                  {errors.userid && (
                    <span className="error-message">{errors.userid}</span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="companyid">
                    Company <span className="required">*</span>
                  </label>
                  <select
                    id="companyid"
                    value={formData.companyid}
                    onChange={(e) => setFormData({ ...formData, companyid: e.target.value })}
                    className={errors.companyid ? 'error' : ''}
                    disabled={editMode}
                  >
                    <option value="">-- Select Company --</option>
                    {companies.map(company => (
                      <option key={company.companyid} value={company.companyid}>
                        {company.companycode} - {company.companyname}
                      </option>
                    ))}
                  </select>
                  {errors.companyid && (
                    <span className="error-message">{errors.companyid}</span>
                  )}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.isdefault}
                      onChange={(e) => setFormData({ ...formData, isdefault: e.target.checked })}
                    />
                    <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Star size={16} style={{ color: '#f59e0b' }} />
                      Set as Default Company
                    </span>
                  </label>
                  <small style={{ color: '#6B7280', marginTop: '4px', display: 'block' }}>
                    This company will be selected automatically on login
                  </small>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={closeModal}
                  disabled={submitLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={submitLoading}
                >
                  {submitLoading ? 'Saving...' : (editMode ? 'Update' : 'Grant Access')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserCompanyAccess;